import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const REFERENCE_ROUTES = {
  PRE_SURVEY: (referenceId) => `/admin/bumdes/pre-survey/${referenceId}`,
  VECI: (referenceId) => `/admin/bumdes/survey-veci/review/${referenceId}`,
  CERTIFICATE: (referenceId, metadata) =>
    metadata?.batchId ? `/admin/certificates?batch=${metadata.batchId}` : `/admin/certificates`,
  CLP_ATTENDANCE_SESSION: (referenceId) => `/admin/attendances/${referenceId}`,
};

function timeAgo(dateString) {
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (seconds < 60) return 'Baru saja';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} menit lalu`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} jam lalu`;
  const days = Math.floor(hours / 24);
  return `${days} hari lalu`;
}

const NotificationBell = () => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.getUnreadNotificationCount();
      setUnreadCount(res?.data?.unreadCount || 0);
    } catch (error) {
      // Silent fail — bell just won't update this cycle.
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.getNotifications({ page: 1, pageSize: 10 });
      setNotifications(res?.data?.notifications || []);
      setUnreadCount(res?.data?.unreadCount || 0);
    } catch (error) {
      // Silent fail — dropdown just shows empty state.
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) {
      fetchNotifications();
    }
  };

  const handleItemClick = async (notification) => {
    if (!notification.isRead) {
      try {
        await api.markNotificationRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        // Ignore — navigation still proceeds.
      }
    }
    setIsOpen(false);
    const buildRoute = REFERENCE_ROUTES[notification.referenceType];
    if (buildRoute && notification.referenceId) {
      navigate(buildRoute(notification.referenceId, notification.metadata));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      // Ignore.
    }
  };

  return (
    <div
      className='dropdown'
      ref={dropdownRef}
    >
      <button
        type='button'
        className='btn btn-light rounded-circle position-relative d-flex align-items-center justify-content-center border'
        style={{ width: '38px', height: '38px' }}
        onClick={toggleDropdown}
      >
        <i className='bi bi-bell fs-5 text-dark'></i>
        {unreadCount > 0 && (
          <span
            className='position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger'
            style={{ fontSize: '0.6rem' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className='dropdown-menu dropdown-menu-end shadow-lg border-0 rounded-4 p-0 show overflow-hidden'
          style={{ width: '380px', maxWidth: '92vw', right: 0, left: 'auto', marginTop: '12px' }}
        >
          <div className='d-flex align-items-center justify-content-between px-4 py-3 border-bottom bg-white'>
            <h6 className='fw-bold mb-0'>Notifikasi</h6>
            {unreadCount > 0 && (
              <button
                type='button'
                className='btn btn-link btn-sm p-0 text-decoration-none fw-semibold'
                onClick={handleMarkAllRead}
              >
                Tandai semua dibaca
              </button>
            )}
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {isLoading && (
              <div className='text-center text-muted small py-5'>Memuat...</div>
            )}

            {!isLoading && notifications.length === 0 && (
              <div className='text-center text-muted small py-5 px-4'>
                <i className='bi bi-bell-slash d-block mb-2' style={{ fontSize: '1.75rem', opacity: 0.4 }}></i>
                Belum ada notifikasi.
              </div>
            )}

            {!isLoading &&
              notifications.map((notification, index) => (
                <button
                  key={notification.id}
                  type='button'
                  className={`dropdown-item d-block px-4 py-3 ${
                    index < notifications.length - 1 ? 'border-bottom' : ''
                  } ${notification.isRead ? '' : 'bg-light'}`}
                  onClick={() => handleItemClick(notification)}
                >
                  <div className='d-flex align-items-start gap-3'>
                    <span
                      className='rounded-circle mt-2 flex-shrink-0'
                      style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: notification.isRead ? 'transparent' : '#dc3545',
                      }}
                    ></span>
                    <div className='flex-grow-1 text-wrap lh-base'>
                      <div className='fw-semibold small mb-1'>{notification.title}</div>
                      <div className='text-muted small mb-2'>{notification.message}</div>
                      <div className='text-muted' style={{ fontSize: '0.72rem' }}>
                        {timeAgo(notification.createdAt)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
